import { SEO } from '../components/SEO'
import { motion } from 'framer-motion'
import {
  MessageSquare, Wrench, Brain, Layers, Lock, Zap,
  FileText, Globe, Mic, Image, Monitor, Database,
  Settings, Shield, Clock, TrendingUp, Film, Scissors
} from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { t } from '../locales/translations'

const Features = () => {
  const { language } = useLanguage()
  
  const featureCategories = [
    {
      key: 'interaction',
      icon: MessageSquare,
      color: 'from-primary-dark to-primary',
      features: [
        { icon: Brain, key: 'smartChat' },
        { icon: Zap, key: 'taskExec' },
        { icon: TrendingUp, key: 'autoOpt' },
        { icon: Database, key: 'expLearn' },
      ]
    },
    {
      key: 'tools',
      icon: Wrench,
      color: 'from-primary to-[#ff8da8]',
      features: [
        { icon: Monitor, key: 'sysControl' },
        { icon: FileText, key: 'fileOps' },
        { icon: Image, key: 'mediaProc' },
        { icon: Globe, key: 'network' },
      ]
    },
    {
      key: 'media',
      icon: Film,
      color: 'from-[#ff8f68] to-secondary',
      features: [
        { icon: Film, key: 'videoEdit' },
        { icon: Scissors, key: 'videoFx' },
        { icon: Image, key: 'imageProc' },
        { icon: Layers, key: 'batch' },
      ]
    },
    {
      key: 'multimodal',
      icon: Layers,
      color: 'from-[#37b8aa] to-[#79d7ca]',
      features: [
        { icon: Image, key: 'ocr' },
        { icon: Mic, key: 'tts' },
        { icon: FileText, key: 'docParse' },
        { icon: Brain, key: 'smartInput' },
      ]
    },
    {
      key: 'memory',
      icon: Database,
      color: 'from-[#f08f2c] to-secondary',
      features: [
        { icon: Clock, key: 'history' },
        { icon: Brain, key: 'longTerm' },
        { icon: FileText, key: 'knowledge' },
        { icon: TrendingUp, key: 'experience' },
      ]
    },
    {
      key: 'security',
      icon: Shield,
      color: 'from-primary-dark to-[#ff8da8]',
      features: [
        { icon: Lock, key: 'permission' },
        { icon: Shield, key: 'encryption' },
        { icon: Settings, key: 'userSys' },
        { icon: FileText, key: 'audit' },
      ]
    },
    {
      key: 'models',
      icon: Layers,
      color: 'from-primary to-secondary',
      features: [
        { icon: Layers, key: 'multiModel' },
        { icon: Zap, key: 'flexSwitch' },
        { icon: Globe, key: 'cloudSync' },
        { icon: TrendingUp, key: 'tokenMgmt' },
      ]
    }
  ]

  return (
    <>
      <SEO />
    <div className="pb-20 pt-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20 text-center"
        >
          <div className="mb-5 inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs uppercase tracking-[0.22em] text-primary">
            {language === 'zh' ? 'Platform Capability Map' : 'Platform Capability Map'}
          </div>
          <h1 className="mb-6 text-5xl font-bold md:text-5xl">
            <span className="text-text-primary">{t(language, 'features.title')}</span>
          </h1>
          <p className="mx-auto max-w-3xl whitespace-pre-line text-xl text-text-secondary md:text-2xl">
            {t(language, 'features.subtitle')}
          </p>
        </motion.div>

        {/* Feature Categories */}
        <div className="space-y-20">
          {featureCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1 }}
            >
              {/* Category Header */}
              <div className="mb-8 flex items-center space-x-4">
                <div className={`flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${category.color} text-white shadow-[0_16px_34px_rgba(255,95,135,0.18)]`}>
                  <category.icon size={32} />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-text-primary">{t(language, `features.categories.${category.key}.title`)}</h2>
                  <div className={`h-1 w-24 bg-gradient-to-r ${category.color} rounded-full mt-2`}></div>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                {category.features.map((feature, featureIndex) => (
                  <motion.div
                    key={feature.key}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: featureIndex * 0.05 }}
                    className="group cursor-pointer rounded-xl p-6 transition-all glass-effect hover:-translate-y-1 hover:border-primary/35 hover:bg-primary/5"
                  >
                    <feature.icon 
                      className="mb-4 text-primary group-hover:scale-110 transition-transform" 
                      size={36} 
                    />
                    <h3 className="mb-2 text-lg font-semibold text-text-primary">{t(language, `features.categories.${category.key}.${feature.key}.name`)}</h3>
                    <p className="text-sm text-text-secondary">{t(language, `features.categories.${category.key}.${feature.key}.desc`)}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 rounded-3xl p-12 glass-effect"
        >
          <div className="grid grid-cols-1 gap-8 text-center md:grid-cols-3">
            <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_12px_28px_rgba(27,35,51,0.05)]">
              <div className="text-5xl font-bold text-primary-dark mb-2">6</div>
              <div className="text-text-secondary">{t(language, 'features.stats.tools')}</div>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_12px_28px_rgba(27,35,51,0.05)]">
              <div className="text-5xl font-bold text-primary-dark mb-2">3</div>
              <div className="text-text-secondary">{t(language, 'features.stats.mediaFeatures')}</div>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_12px_28px_rgba(27,35,51,0.05)]">
              <div className="text-5xl font-bold text-primary-dark mb-2">1</div>
              <div className="text-text-secondary">{t(language, 'features.stats.possibilities')}</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  )
}

export default Features
